import { create } from 'zustand';
import type {
  Project,
  ProjectSummary,
  Preventivo,
  Finanziamento,
  Notaio,
  GanttTask,
  CostoExtra,
  EstimateLevel
} from '../types';
import { api } from '../services/api';

interface ProjectState {
  // Data
  projects: ProjectSummary[];
  currentProject: Project | null;
  estimateLevel: EstimateLevel;

  // Loading states
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;

  // Project list actions
  fetchProjects: () => Promise<void>;
  createProject: (name: string, description?: string) => Promise<Project>;
  deleteProject: (id: string) => Promise<void>;

  // Current project actions
  selectProject: (id: string) => Promise<void>;
  updateProject: (data: Partial<Project>) => Promise<void>;
  clearCurrentProject: () => void;

  // Estimate level
  setEstimateLevel: (level: EstimateLevel) => void;

  // Preventivi actions
  addPreventivo: (data: Omit<Preventivo, 'id' | 'projectId' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  updatePreventivo: (id: string, data: Partial<Preventivo>) => Promise<void>;
  deletePreventivo: (id: string) => Promise<void>;

  // Finanziamenti actions
  addFinanziamento: (data: Omit<Finanziamento, 'id' | 'projectId' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  updateFinanziamento: (id: string, data: Partial<Finanziamento>) => Promise<void>;
  deleteFinanziamento: (id: string) => Promise<void>;

  // Notaio actions
  saveNotaio: (data: Omit<Notaio, 'id' | 'projectId' | 'createdAt' | 'updatedAt'>) => Promise<void>;

  // Gantt actions
  addGanttTask: (data: Omit<GanttTask, 'id' | 'projectId' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  updateGanttTask: (id: string, data: Partial<GanttTask>) => Promise<void>;
  deleteGanttTask: (id: string) => Promise<void>;

  // Costi extra actions
  addCostoExtra: (data: Omit<CostoExtra, 'id' | 'projectId' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  updateCostoExtra: (id: string, data: Partial<CostoExtra>) => Promise<void>;
  deleteCostoExtra: (id: string) => Promise<void>;

  // Error handling
  clearError: () => void;
}

export const useProjectStore = create<ProjectState>((set, get) => ({
  projects: [],
  currentProject: null,
  estimateLevel: 'med',
  isLoading: false,
  isSaving: false,
  error: null,

  fetchProjects: async () => {
    set({ isLoading: true, error: null });
    try {
      const projects = await api.get<ProjectSummary[]>('/projects');
      set({ projects, isLoading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Errore nel caricamento progetti';
      set({ error: message, isLoading: false });
    }
  },

  createProject: async (name, description) => {
    set({ isSaving: true, error: null });
    try {
      const project = await api.post<Project>('/projects', { name, description });
      set((state) => ({
        projects: [{ ...project, _count: { preventivi: 0, finanziamenti: 0, ganttTasks: 0 } }, ...state.projects],
        isSaving: false,
      }));
      return project;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Errore nella creazione progetto';
      set({ error: message, isSaving: false });
      throw err;
    }
  },

  deleteProject: async (id) => {
    set({ isSaving: true, error: null });
    try {
      await api.delete(`/projects/${id}`);
      set((state) => ({
        projects: state.projects.filter((p) => p.id !== id),
        currentProject: state.currentProject?.id === id ? null : state.currentProject,
        isSaving: false,
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Errore nella cancellazione progetto';
      set({ error: message, isSaving: false });
      throw err;
    }
  },

  selectProject: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const project = await api.get<Project>(`/projects/${id}`);
      set({ currentProject: project, isLoading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Errore nel caricamento progetto';
      set({ error: message, isLoading: false });
      throw err;
    }
  },

  updateProject: async (data) => {
    const { currentProject } = get();
    if (!currentProject) return;

    set({ isSaving: true, error: null });
    try {
      const updated = await api.patch<Project>(`/projects/${currentProject.id}`, data);
      set((state) => ({
        currentProject: { ...state.currentProject!, ...updated },
        projects: state.projects.map((p) =>
          p.id === currentProject.id ? { ...p, ...updated } : p
        ),
        isSaving: false,
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Errore nel salvataggio';
      set({ error: message, isSaving: false });
      throw err;
    }
  },

  clearCurrentProject: () => set({ currentProject: null }),

  setEstimateLevel: (level) => set({ estimateLevel: level }),

  // Preventivi
  addPreventivo: async (data) => {
    const { currentProject } = get();
    if (!currentProject) return;

    set({ isSaving: true, error: null });
    try {
      const preventivo = await api.post<Preventivo>('/preventivi', {
        ...data,
        projectId: currentProject.id,
      });
      set((state) => ({
        currentProject: state.currentProject
          ? {
              ...state.currentProject,
              preventivi: [...(state.currentProject.preventivi || []), preventivo],
            }
          : null,
        isSaving: false,
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Errore nel salvataggio preventivo';
      set({ error: message, isSaving: false });
      throw err;
    }
  },

  updatePreventivo: async (id, data) => {
    set({ isSaving: true, error: null });
    try {
      const updated = await api.patch<Preventivo>(`/preventivi/${id}`, data);
      set((state) => ({
        currentProject: state.currentProject
          ? {
              ...state.currentProject,
              preventivi: state.currentProject.preventivi?.map((p) =>
                p.id === id ? updated : p
              ),
            }
          : null,
        isSaving: false,
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Errore nel salvataggio';
      set({ error: message, isSaving: false });
      throw err;
    }
  },

  deletePreventivo: async (id) => {
    set({ isSaving: true, error: null });
    try {
      await api.delete(`/preventivi/${id}`);
      set((state) => ({
        currentProject: state.currentProject
          ? {
              ...state.currentProject,
              preventivi: state.currentProject.preventivi?.filter((p) => p.id !== id),
            }
          : null,
        isSaving: false,
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Errore nella cancellazione';
      set({ error: message, isSaving: false });
      throw err;
    }
  },

  // Finanziamenti
  addFinanziamento: async (data) => {
    const { currentProject } = get();
    if (!currentProject) return;

    set({ isSaving: true, error: null });
    try {
      const finanziamento = await api.post<Finanziamento>('/finanziamenti', {
        ...data,
        projectId: currentProject.id,
      });
      set((state) => ({
        currentProject: state.currentProject
          ? {
              ...state.currentProject,
              finanziamenti: [...(state.currentProject.finanziamenti || []), finanziamento],
            }
          : null,
        isSaving: false,
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Errore nel salvataggio finanziamento';
      set({ error: message, isSaving: false });
      throw err;
    }
  },

  updateFinanziamento: async (id, data) => {
    set({ isSaving: true, error: null });
    try {
      const updated = await api.patch<Finanziamento>(`/finanziamenti/${id}`, data);
      set((state) => ({
        currentProject: state.currentProject
          ? {
              ...state.currentProject,
              finanziamenti: state.currentProject.finanziamenti?.map((f) =>
                f.id === id ? updated : f
              ),
            }
          : null,
        isSaving: false,
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Errore nel salvataggio';
      set({ error: message, isSaving: false });
      throw err;
    }
  },

  deleteFinanziamento: async (id) => {
    set({ isSaving: true, error: null });
    try {
      await api.delete(`/finanziamenti/${id}`);
      set((state) => ({
        currentProject: state.currentProject
          ? {
              ...state.currentProject,
              finanziamenti: state.currentProject.finanziamenti?.filter((f) => f.id !== id),
            }
          : null,
        isSaving: false,
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Errore nella cancellazione';
      set({ error: message, isSaving: false });
      throw err;
    }
  },

  // Notaio
  saveNotaio: async (data) => {
    const { currentProject } = get();
    if (!currentProject) return;

    set({ isSaving: true, error: null });
    try {
      const notaio = await api.post<Notaio>('/notaio', {
        ...data,
        projectId: currentProject.id,
      });
      set((state) => ({
        currentProject: state.currentProject
          ? { ...state.currentProject, notaio }
          : null,
        isSaving: false,
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Errore nel salvataggio costi notaio';
      set({ error: message, isSaving: false });
      throw err;
    }
  },

  // Gantt Tasks
  addGanttTask: async (data) => {
    const { currentProject } = get();
    if (!currentProject) return;

    set({ isSaving: true, error: null });
    try {
      const task = await api.post<GanttTask>('/gantt', {
        ...data,
        projectId: currentProject.id,
      });
      set((state) => ({
        currentProject: state.currentProject
          ? {
              ...state.currentProject,
              ganttTasks: [...(state.currentProject.ganttTasks || []), task],
            }
          : null,
        isSaving: false,
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Errore nel salvataggio task';
      set({ error: message, isSaving: false });
      throw err;
    }
  },

  updateGanttTask: async (id, data) => {
    set({ isSaving: true, error: null });
    try {
      const updated = await api.patch<GanttTask>(`/gantt/${id}`, data);
      set((state) => ({
        currentProject: state.currentProject
          ? {
              ...state.currentProject,
              ganttTasks: state.currentProject.ganttTasks?.map((t) =>
                t.id === id ? updated : t
              ),
            }
          : null,
        isSaving: false,
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Errore nel salvataggio';
      set({ error: message, isSaving: false });
      throw err;
    }
  },

  deleteGanttTask: async (id) => {
    set({ isSaving: true, error: null });
    try {
      await api.delete(`/gantt/${id}`);
      set((state) => ({
        currentProject: state.currentProject
          ? {
              ...state.currentProject,
              ganttTasks: state.currentProject.ganttTasks?.filter((t) => t.id !== id),
            }
          : null,
        isSaving: false,
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Errore nella cancellazione';
      set({ error: message, isSaving: false });
      throw err;
    }
  },

  // Costi Extra
  addCostoExtra: async (data) => {
    const { currentProject } = get();
    if (!currentProject) return;

    set({ isSaving: true, error: null });
    try {
      const costo = await api.post<CostoExtra>('/costi-extra', {
        ...data,
        projectId: currentProject.id,
      });
      set((state) => ({
        currentProject: state.currentProject
          ? {
              ...state.currentProject,
              costiExtra: [...(state.currentProject.costiExtra || []), costo],
            }
          : null,
        isSaving: false,
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Errore nel salvataggio costo extra';
      set({ error: message, isSaving: false });
      throw err;
    }
  },

  updateCostoExtra: async (id, data) => {
    set({ isSaving: true, error: null });
    try {
      const updated = await api.patch<CostoExtra>(`/costi-extra/${id}`, data);
      set((state) => ({
        currentProject: state.currentProject
          ? {
              ...state.currentProject,
              costiExtra: state.currentProject.costiExtra?.map((c) =>
                c.id === id ? updated : c
              ),
            }
          : null,
        isSaving: false,
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Errore nel salvataggio';
      set({ error: message, isSaving: false });
      throw err;
    }
  },

  deleteCostoExtra: async (id) => {
    set({ isSaving: true, error: null });
    try {
      await api.delete(`/costi-extra/${id}`);
      set((state) => ({
        currentProject: state.currentProject
          ? {
              ...state.currentProject,
              costiExtra: state.currentProject.costiExtra?.filter((c) => c.id !== id),
            }
          : null,
        isSaving: false,
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Errore nella cancellazione';
      set({ error: message, isSaving: false });
      throw err;
    }
  },

  clearError: () => set({ error: null }),
}));
